import { type FC, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import SessionTimeoutWarning from "@/Components/SessionTimeoutWarning";
import { useAuth } from "@/hooks/useAuth";
import { useIdleSession } from "@/hooks/useIdleSession";
import { selectLogoutTime, selectWarningTime } from "@/store/adminSlice";
import { setSessionTimeoutOpen } from "@/store/userSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectFeatures } from "@/store/userSlice";


// Nested manager that actually initializes the idle session hook.
const IdleSessionManager: FC<{
  timeoutMs: number;
  warningMs: number;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
}> = ({ timeoutMs, warningMs, isAuthenticated, logout }) => {
  const navigate = useNavigate();
  const SYNC_LOGOUT_KEY = "msal-logout";

  const dispatch = useAppDispatch();

  const { isPrompted, warningSeconds, resetTimer } = useIdleSession({
    timeoutMs,
    warningMs,
    disabled: !isAuthenticated,

    onIdle: async () => {
      localStorage.setItem(SYNC_LOGOUT_KEY, Date.now().toString());
      await logout();
    },

    onActive: () => {
      resetTimer();
    },

    onAction: (_event, timer) => {
      // Do NOT reset timer while warning is visible
      if (!timer?.isPrompted()) {
        resetTimer();
      }
    },
  });

  useEffect(() => {
    dispatch(setSessionTimeoutOpen(isPrompted));
  }, [dispatch, isPrompted]);

  const handleStaySignedIn = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  const handleLogoutNow = useCallback(async () => {
    localStorage.setItem(SYNC_LOGOUT_KEY, Date.now().toString());
    await logout();
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleStorage = (event: StorageEvent) => {
      if (event.key === SYNC_LOGOUT_KEY) {
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [isAuthenticated, navigate]);

  return (
    <SessionTimeoutWarning
      open={isAuthenticated && isPrompted}
      warningSeconds={warningSeconds}
      onStaySignedIn={handleStaySignedIn}
      onLogout={handleLogoutNow}
    />
  );
};

const IdleSessionHandler: FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const logoutTime = useAppSelector(selectLogoutTime);
  const warningTime = useAppSelector(selectWarningTime);

  // Only initialize the idle session after we have both values
  const shouldInit = Boolean(logoutTime && warningTime);

const dashboardFeatures = useAppSelector(selectFeatures);

const hasRoles = dashboardFeatures?.some(
  (feature) => feature.access === true || feature.access === 1
);

const AUTO_LOGOUT_MS = logoutTime ? logoutTime * 60 * 1000 : 0;
const WARNING_BEFORE_MS = warningTime ? warningTime * 60 * 1000 : 0;

useEffect(() => {
  if (!isAuthenticated || hasRoles || !AUTO_LOGOUT_MS) return;

  const timer = setTimeout(async () => {
    await logout();
  }, AUTO_LOGOUT_MS);

  return () => clearTimeout(timer);
}, [isAuthenticated, hasRoles, AUTO_LOGOUT_MS, logout]);

if (!shouldInit || !hasRoles) return null;


  return (
    <IdleSessionManager
      timeoutMs={AUTO_LOGOUT_MS}
      warningMs={WARNING_BEFORE_MS}
      isAuthenticated={isAuthenticated}
      logout={logout}
    />
  );
};

export default IdleSessionHandler;
